<template>
    <v-dialog v-model="dialog" max-width="700px" persistent>
        <template v-slot:activator="{ on: dialog, attrs }">
            <div v-if="$scopedSlots.activator" v-bind="attrs" v-on="dialog">
                <slot name="activator"></slot>
            </div>
            <v-btn v-else color="primary" v-bind="attrs" v-on="dialog">
                <v-icon left>mdi-plus</v-icon>
                Users
            </v-btn>
        </template>

        <v-card>
            <v-card-title class="flex-nowrap align-start">
                Invite Organization Users
                <v-spacer></v-spacer>
                <v-btn @click="closeDialog" icon><v-icon>clear</v-icon></v-btn>
            </v-card-title>
            <v-card-text>
                <v-radio-group :disabled="inviteActionInProgress" v-model="invitationType" class="mt-0 pt-0">
                    <v-container fluid class="pa-0">
                        <v-row>
                            <v-col v-for="mode in invitationTypes" :key="mode.type" cols="12" sm="6">
                                <v-card flat :color="invitationType === mode.value ? 'grey lighten-3' : ''" outlined>
                                    <v-radio :value="mode.value" class="pl-4">
                                        <template v-slot:label>
                                            <v-card-text class="text-button">{{ mode.type }}</v-card-text>
                                        </template>
                                    </v-radio>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-radio-group>
                <p>{{ selectedInvitationDescription }}</p>
                <InviteEmailsInput
                    ref="inviteEmailsInput"
                    v-model="emailsInput"
                    :valid.sync="valid"
                    :disabled="inviteActionInProgress"
                    :showGenerateLinkOnlyToggle="true"
                    :generateInvitationLinksOnly.sync="generateInvitationLinksOnly"
                    :invitationErrorState="{
                        showDialog: showInvitationErrorsDialog,
                        errorType: validationErrorType,
                        invalidEmails: invalidEmailsList,
                        foundInvalidEmails,
                        backendError,
                        emailsValidationError
                    }"
                    @input="updateEmailString"
                    @proceedWithTheInvitation="inviteUsers($event.value)"
                    @validateEmailsAgain="validateEmailsAndInviteUsers($event.value)" />

                <v-alert v-if="error" color="error" class="mt-4" text>
                    <div class="d-flex flex-column">
                        <span class="font-weight-bold">Invitation Failure Error</span>
                        Failed to invite the following users
                        <ul>
                            <li v-for="(email, index) in failedInvitations" :key="index">
                                {{ email }}
                            </li>
                        </ul>
                    </div>
                </v-alert>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <div>
                    <v-btn v-if="inviteSuccess" color="success" icon>
                        <v-icon>mdi-check</v-icon>
                    </v-btn>
                    <v-btn v-else :loading="inviteActionInProgress" @click="handleInvite" :disabled="!valid || inviteActionInProgress" color="primary" text>
                        Invite
                    </v-btn>
                </div>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { inviteEmails } from '@/mixins/inviteEmails'
import InviteEmailsInput from '@/components/InviteEmailsInput.vue'

export default {
    name: 'OrganizationInvite',
    mixins: [inviteEmails],
    components: { InviteEmailsInput },
    data() {
        return {
            dialog: false,
            inviteSuccess: false,
            inviteSuccessTimeoutId: null,
            invitationType: 'orgManagers',
            invitationTypes: [
                {
                    type: 'Organization Manager',
                    description: 'Organization users can create and delete space, view all instances, and request the addition of new vendor datasets.',
                    value: 'orgManagers'
                },
                {
                    type: 'Faculty',
                    description: 'Faculty can create and delete space, and invite colleagues or students to join a space of which the faculty is an admin.',
                    value: 'orgFaculties'
                }
            ]
        }
    },
    watch: {
        numInvitationsToSend(nextVal, prevVal) {
            if (prevVal > 0 && nextVal === 0 && !this.error) {
                this.inviteSuccess = true
                if (this.inviteSuccessTimeoutId) {
                    clearTimeout(this.inviteSuccessTimeoutId)
                }
                this.inviteSuccessTimeoutId = setTimeout(() => {
                    this.closeDialog()
                }, 2000)
            }
        }
    },
    computed: {
        selectedInvitationDescription() {
            const selectedMode = this.invitationTypes.find(mode => mode.value === this.invitationType)
            return selectedMode ? selectedMode.description : ''
        },
        requestUrl() {
            let apiURL
            if (this.invitationType === 'orgManagers') {
                apiURL = '/invitations/invite_org_manager/' + this.$route.params.oid
            } else if (this.invitationType === 'orgFaculties') {
                apiURL = '/invitations/invite_org_faculty/' + this.$route.params.oid
            }
            return apiURL
        }
    },
    methods: {
        handleInvite() {
            this.inviteSuccess = false
            this.validateEmailsAndInviteUsers(true)
        },
        openDialog() {
            this.dialog = true
        },
        closeDialog() {
            if (this.inviteSuccessTimeoutId) {
                clearTimeout(this.inviteSuccessTimeoutId)
                this.inviteSuccessTimeoutId = null
            }
            this.dialog = false
            this.inviteSuccess = false
            this.invitationType = 'orgManagers'
            this.resetInviteEmailsForm()
        }
    },
    beforeDestroy() {
        if (this.inviteSuccessTimeoutId) {
            clearTimeout(this.inviteSuccessTimeoutId)
        }
    }
}
</script>
